import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook for managing recording timer functionality
 * Handles start, pause, resume, and stop operations with proper state management
 * Supports external synchronization for multi-user scenarios
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onTimerUpdate - Callback when timer state changes (for sync)
 * @param {Function} options.onSyncRequest - Callback to request timer sync
 * @returns {Object} Timer state and control functions
 */
export const useRecordingTimer = (options = {}) => {
  // Core timer state
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [displayTime, setDisplayTime] = useState('00:00:00');
  
  // Internal state for accurate timing
  const startTimeRef = useRef(null);
  const pauseTimeRef = useRef(null);
  const totalPausedDurationRef = useRef(0);
  const intervalRef = useRef(null);
  
  // Prevent multiple rapid clicks
  const lastActionTimeRef = useRef(0);
  const ACTION_DEBOUNCE_MS = 100;

  /**
   * Format seconds into HH:MM:SS format
   * @param {number} totalSeconds - Total seconds to format
   * @returns {string} Formatted time string
   */
  const formatTime = useCallback((totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  /**
   * Calculate the current elapsed time accounting for pauses
   * @returns {number} Total elapsed seconds
   */
  const calculateElapsedTime = useCallback(() => {
    if (!startTimeRef.current) return 0;
    
    const now = Date.now();
    const totalElapsed = now - startTimeRef.current;
    
    // Calculate current pause duration only if we're currently paused
    const currentPauseDuration = isPaused && pauseTimeRef.current ? now - pauseTimeRef.current : 0;
    
    // Total paused time = previously accumulated paused time + current pause duration
    const totalPaused = totalPausedDurationRef.current + currentPauseDuration;
    
    return Math.floor((totalElapsed - totalPaused) / 1000);
  }, [isPaused]);

  /**
   * Update the display time
   */
  const updateDisplayTime = useCallback(() => {
    const elapsedSeconds = calculateElapsedTime();
    setDisplayTime(formatTime(elapsedSeconds));
  }, [calculateElapsedTime, formatTime]);

  /**
   * Start the timer
   */
  const start = useCallback(() => {
    const now = Date.now();
    
    // Debounce rapid clicks
    if (now - lastActionTimeRef.current < ACTION_DEBOUNCE_MS) {
      return;
    }
    lastActionTimeRef.current = now;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Reset all state
    startTimeRef.current = now;
    pauseTimeRef.current = null;
    totalPausedDurationRef.current = 0;
    setIsPaused(false);
    setIsRunning(true);
    
    // Start the interval
    intervalRef.current = setInterval(updateDisplayTime, 1000);
    
    // Update display immediately
    updateDisplayTime();

    // Notify external systems of timer update
    if (options.onTimerUpdate) {
      options.onTimerUpdate({
        isRunning: true,
        isPaused: false,
        startTime: now,
        totalPausedDuration: 0,
        lastPauseTime: null
      });
    }
  }, [updateDisplayTime, options.onTimerUpdate]);

  /**
   * Pause the timer
   */
  const pause = useCallback(() => {
    const now = Date.now();
    
    // Debounce rapid clicks
    if (now - lastActionTimeRef.current < ACTION_DEBOUNCE_MS) {
      return;
    }
    lastActionTimeRef.current = now;

    if (!isRunning || isPaused) return;

    // Clear the interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Record pause time
    pauseTimeRef.current = now;
    setIsPaused(true);

    // Notify external systems of timer update
    if (options.onTimerUpdate) {
      options.onTimerUpdate({
        isRunning: true,
        isPaused: true,
        startTime: startTimeRef.current,
        totalPausedDuration: totalPausedDurationRef.current,
        lastPauseTime: now
      });
    }
  }, [isRunning, isPaused, options.onTimerUpdate]);

  /**
   * Resume the timer from pause
   */
  const resume = useCallback(() => {
    const now = Date.now();
    
    // Debounce rapid clicks
    if (now - lastActionTimeRef.current < ACTION_DEBOUNCE_MS) {
      return;
    }
    lastActionTimeRef.current = now;

    if (!isRunning || !isPaused) return;

    // Add the current pause duration to total paused time
    if (pauseTimeRef.current) {
      totalPausedDurationRef.current += now - pauseTimeRef.current;
      pauseTimeRef.current = null;
    }

    // Clear any existing interval and start new one
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    setIsPaused(false);
    intervalRef.current = setInterval(updateDisplayTime, 1000);
    
    // Update display immediately
    updateDisplayTime();

    // Notify external systems of timer update
    if (options.onTimerUpdate) {
      options.onTimerUpdate({
        isRunning: true,
        isPaused: false,
        startTime: startTimeRef.current,
        totalPausedDuration: totalPausedDurationRef.current,
        lastPauseTime: null
      });
    }
  }, [isRunning, isPaused, updateDisplayTime, options.onTimerUpdate]);

  /**
   * Stop the timer and reset to initial state
   */
  const stop = useCallback(() => {
    const now = Date.now();
    
    // Debounce rapid clicks
    if (now - lastActionTimeRef.current < ACTION_DEBOUNCE_MS) {
      return;
    }
    lastActionTimeRef.current = now;

    // Clear interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Reset all state
    startTimeRef.current = null;
    pauseTimeRef.current = null;
    totalPausedDurationRef.current = 0;
    setIsRunning(false);
    setIsPaused(false);
    setDisplayTime('00:00:00');

    // Notify external systems of timer update
    if (options.onTimerUpdate) {
      options.onTimerUpdate({
        isRunning: false,
        isPaused: false,
        startTime: null,
        totalPausedDuration: 0,
        lastPauseTime: null
      });
    }
  }, [options.onTimerUpdate]);

  /**
   * Get current timer state for external use
   */
  const getTimerState = useCallback(() => ({
    isRunning,
    isPaused,
    displayTime,
    elapsedSeconds: calculateElapsedTime()
  }), [isRunning, isPaused, displayTime, calculateElapsedTime]);

  /**
   * Sync timer with external state (for multi-user scenarios)
   */
  const syncWithExternalState = useCallback((externalState) => {
    const { isRunning: extIsRunning, isPaused: extIsPaused, startTime, totalPausedDuration, lastPauseTime } = externalState;
    
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Update internal state to match external state
    startTimeRef.current = startTime;
    totalPausedDurationRef.current = totalPausedDuration || 0;
    pauseTimeRef.current = lastPauseTime;
    setIsRunning(extIsRunning);
    setIsPaused(extIsPaused);

    // Start interval if running and not paused
    if (extIsRunning && !extIsPaused) {
      intervalRef.current = setInterval(updateDisplayTime, 1000);
    }

    // Update display immediately
    updateDisplayTime();
  }, [updateDisplayTime]);

  /**
   * Request timer synchronization (for new users or rejoiners)
   */
  const requestSync = useCallback(() => {
    if (options.onSyncRequest) {
      options.onSyncRequest();
    }
  }, [options.onSyncRequest]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    // State
    isRunning,
    isPaused,
    displayTime,
    
    // Actions
    start,
    pause,
    resume,
    stop,
    
    // Utilities
    getTimerState,
    
    // Synchronization
    syncWithExternalState,
    requestSync
  };
};

export default useRecordingTimer;
